import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

const LAST_SEEN_THROTTLE_MS = 5 * 60 * 1000;

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const isRefreshTokenError = (message?: string) =>
  !!message && /refresh token/i.test(message);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const lastSeenRef = useRef<number>(0);

  const runSignedInSideEffects = (session: Session) => {
    const now = Date.now();

    if (now - lastSeenRef.current > LAST_SEEN_THROTTLE_MS) {
      lastSeenRef.current = now;
      supabase
        .from("profiles")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("user_id", session.user.id)
        .then();
    }

    const pendingCode =
      localStorage.getItem("kaufyard_referral_pending") ||
      localStorage.getItem("kaufyard_referral_code");

    if (pendingCode) {
      // `apply_referral` reports a refusal as a *value*, not as a rejection, so
      // this `.then()` ran for a refused call too — and it cleared the pending
      // code. A failed apply therefore burned the invite permanently: nothing
      // was credited and there was no second chance on the next sign-in. The
      // code is now cleared only by a call that did not fail.
      supabase
        .rpc("apply_referral", {
          p_referred_id: session.user.id,
          p_code: pendingCode,
        })
        .then(
          ({ error }) => {
            if (error) return;
            localStorage.removeItem("kaufyard_referral_pending");
            localStorage.removeItem("kaufyard_referral_code");
          },
          () => {
            // A dropped connection is not an answer either. Keep the code: the
            // next sign-in retries it. A permanently invalid code costs one
            // request per sign-in, which is cheaper than a lost referral.
            //
            // The rejection handler is the second argument because the builder
            // only types as `PromiseLike`, so there is no `.catch()` to chain.
          },
        );
    }
  };

  useEffect(() => {
    let mounted = true;
    let lastUserId: string | null = null;

    const applyUser = (nextUser: User | null) => {
      const nextId = nextUser?.id ?? null;
      if (nextId === lastUserId) {
        // Same identity (e.g. TOKEN_REFRESHED) — keep reference stable so
        // dependents (realtime channels, effects keyed on user) do not
        // churn on every token refresh.
        return;
      }
      lastUserId = nextId;
      setUser(nextUser);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      applyUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) runSignedInSideEffects(session);
    });

    (async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (!mounted) return;

      if (error && isRefreshTokenError(error.message)) {
        await supabase.auth.signOut({ scope: "local" });
        if (!mounted) return;
        applyUser(null);
        setLoading(false);
        return;
      }

      applyUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) runSignedInSideEffects(session);
    })();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
